import {
  Text,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  ButtonGroup,
} from '@shopify/polaris'
import { Method } from '../../common/types'
import { useEffect, useState } from 'react'
import { useSuspenseQueries } from '@tanstack/react-query'
import { settingsCheckAppBlockQueryOptions } from '@/common/queries.ts'

type Props = {
  enabled: boolean
  isLoading: boolean
  toggleEnabled: () => void
  themeVersion: string
  methods: Partial<Method>[] | undefined
  onboarded: boolean
}

const SetupBanner = ({
  enabled,
  themeVersion,
  toggleEnabled,
  isLoading,
  methods,
  onboarded,
}: Props) => {
  const [isPolling, setIsPolling] = useState(false)

  const [{ data: hasAppBlock }] = useSuspenseQueries({
    queries: [
      {
        ...settingsCheckAppBlockQueryOptions(),
        refetchInterval: isPolling ? 3000 : false,
        refetchIntervalInBackground: true,
      },
    ],
  })

  const hasEnabledMethodAndDays = methods?.some((method) => {
    return method?.enabled && (method?.enabledDays || []).length > 0
  })

  const themeLink = `https://admin.shopify.com/store/${
    shopify?.config?.shop ? shopify.config.shop.split('.')[0] : ''
  }/themes/current/editor?template=product`

  const appEmbedLink = `https://admin.shopify.com/store/${
    shopify?.config?.shop ? shopify.config.shop.split('.')[0] : ''
  }/themes/current/editor?template=product&addAppBlockId=${
    import.meta.env.VITE_APP_EMBED_ID
  }/bloom&target=mainSection`

  useEffect(() => {
    if (hasAppBlock) {
      setIsPolling(false)
    }
  }, [hasAppBlock])

  const isLegacy = themeVersion === 'legacy'

  if (!onboarded && (!enabled || !hasEnabledMethodAndDays || !hasAppBlock)) {
    return null
  }

  return (
    <>
      <Layout.Section variant="oneHalf">
        <Card>
          <BlockStack gap="200">
            <InlineStack gap="200">
              <Text as="p" fontWeight="bold">
                {isLegacy ? 'Script tag status' : 'App block status'}
              </Text>
              {isLegacy ? (
                <Badge tone="success">Active</Badge>
              ) : (
                <Badge tone={hasAppBlock ? 'success' : undefined}>
                  {hasAppBlock ? 'Active' : 'Inactive'}
                </Badge>
              )}
            </InlineStack>
            {isLegacy ? (
              <Text as="p">The Bloom script tag is active in your theme.</Text>
            ) : (
              <Text as="p">
                {hasAppBlock
                  ? 'The Bloom app embed is active in your theme.'
                  : 'Activate the app embed by clicking the button below.'}
              </Text>
            )}

            {isLegacy ? (
              <ButtonGroup>
                <Button variant="primary" disabled={true}>
                  Activated
                </Button>
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                <Button
                  variant={hasAppBlock ? 'secondary' : 'primary'}
                  loading={isPolling}
                  onClick={() => {
                    if (hasAppBlock) {
                      open(themeLink, '_blank')
                    } else {
                      setIsPolling(true)
                      open(appEmbedLink, '_blank')
                    }
                  }}
                >
                  {hasAppBlock ? 'Go to theme' : 'Activate app embed'}
                </Button>
                <Button
                  variant="plain"
                  target="_blank"
                  url="https://docs.shopside.com.au/article/15-using-the-bloom-app-block"
                >
                  Learn more
                </Button>
              </ButtonGroup>
            )}
          </BlockStack>
        </Card>
      </Layout.Section>
      <Layout.Section variant="oneHalf">
        <Card>
          <BlockStack gap="200">
            <InlineStack gap="200">
              <Text as="p" fontWeight="bold">
                App status
              </Text>
              <Badge tone={enabled ? 'success' : undefined}>
                {enabled ? 'Active' : 'Inactive'}
              </Badge>
            </InlineStack>
            <Text as="p">
              {enabled
                ? 'Deactivate Bloom by clicking the button below.'
                : 'Activate Bloom by clicking the button below.'}
            </Text>
            <ButtonGroup>
              <Button
                variant={!enabled ? 'primary' : 'secondary'}
                loading={isLoading}
                onClick={toggleEnabled}
              >
                {enabled ? 'Deactivate' : 'Activate'}
              </Button>
            </ButtonGroup>
          </BlockStack>
        </Card>
      </Layout.Section>
    </>
  )
}

export default SetupBanner
